import React from "react";
import { Crown, MapPin, MessageCircle, MonitorPlay, School, Star, Users, ZoomIn } from "lucide-react";
import { ONLINE_TUTORS } from "@/components/app/TutorOnlineBlock";

const CEO = {
  name: "Norhayati Setta",
  role: { ms: "CEO & Founder", en: "CEO & Founder" },
  org: "Program Membaca PMM · Setta Group Holdings Sdn. Bhd.",
  photo: "/landing/online/ceo.webp",
  poster: "/landing/online/poster-ceo.jpg",
};

/* Tier pengurusan — key merujuk objek MANAGERS dalam Landing.jsx (untuk pautan WhatsApp) */
const buildManagers = (sabahPoster) => [
  { key: "fizikal", name: "Shafie Bin Jais", role: { ms: "Pengurus", en: "Manager" }, area: { ms: "Kelas Fizikal", en: "Physical Class" }, icon: School, photo: "/landing/pmm/shafie.webp", poster: "/landing/pmm/poster-shafie.jpg" },
  { key: "sabah", name: "Cikgu Imran", role: { ms: "Pengurus", en: "Manager" }, area: { ms: "Kelas PMM Sabah", en: "PMM Sabah Class" }, icon: MapPin, photo: "/landing/pmm/imran.webp", poster: sabahPoster },
  { key: "online", name: "Nurzarith Sofea", role: { ms: "Penolong Pengurus", en: "Assistant Manager" }, area: { ms: "Kelas Online", en: "Online Class" }, icon: MonitorPlay, photo: "/landing/online/zarith.webp", poster: "/landing/online/poster-zarith.jpg" },
];

const goldText = { backgroundImage: "linear-gradient(180deg,#f5e08d,#f0a500)", WebkitBackgroundClip: "text", backgroundClip: "text" };

/* Bingkai potret gerbang (arch) dgn cincin emas */
function ArchPortrait({ src, alt, onClick, size = "md", testId }) {
  const w = size === "lg" ? "w-44 sm:w-56" : "w-32 sm:w-40";
  return (
    <button type="button" onClick={onClick} className={`group relative mx-auto block ${w}`} data-testid={testId}>
      <div className="absolute -inset-1.5 rounded-t-full rounded-b-[1.4rem] bg-gradient-to-b from-yellow-200 via-yellow-500 to-yellow-700 opacity-90 blur-[1px]" />
      <div className="relative aspect-[4/5] overflow-hidden rounded-t-full rounded-b-2xl bg-gradient-to-b from-white via-navy-100 to-navy-300">
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-orange-800/80 to-transparent" />
        <img src={src} alt={alt} loading="lazy" className="absolute bottom-0 left-1/2 h-[94%] w-auto max-w-none -translate-x-1/2 object-contain object-bottom transition-transform duration-500 group-hover:scale-105" />
        <span className="absolute right-2 top-1/4 grid h-7 w-7 place-items-center rounded-full bg-navy-950/75 text-yellow-300 opacity-0 ring-1 ring-yellow-400/60 transition-opacity duration-300 group-hover:opacity-100"><ZoomIn className="h-3.5 w-3.5" /></span>
      </div>
    </button>
  );
}

const VLine = ({ className = "" }) => <div className={`mx-auto w-[3px] rounded-full bg-gradient-to-b from-yellow-300 to-yellow-600 ${className}`} />;

export default function LeadershipChart({ ms, setLightbox, managers, sabahPoster }) {
  const L = (m, e) => (ms ? m : e);
  const team = buildManagers(sabahPoster);

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-navy-950 via-navy-900 to-navy-950" data-testid="landing-leadership-section">
      <div className="h-1 w-full bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
      <div className="pointer-events-none absolute -left-32 top-24 h-80 w-80 rounded-full bg-orange-600/20 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-24 h-80 w-80 rounded-full bg-yellow-500/10 blur-3xl" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "radial-gradient(#f5e08d 1px, transparent 1px)", backgroundSize: "26px 26px" }} />

      <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-20">
        {/* Tajuk */}
        <div className="text-center">
          <div className="pmm-pop flex items-center justify-center gap-2 text-xs font-extrabold uppercase tracking-[0.3em] text-yellow-300">
            <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" /> {L("Carta Organisasi", "Organisation Chart")} <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
          </div>
          <h2 className="pmm-pop mt-2 font-display text-3xl font-extrabold uppercase leading-tight text-white sm:text-5xl" style={{ animationDelay: "0.1s" }}>
            {L("Barisan ", "PMM ")}<span className="text-transparent" style={goldText}>{L("Kepimpinan", "Leadership")}</span>{L(" PMM", " Team")}
          </h2>
          <p className="pmm-pop mt-2 font-script text-2xl text-yellow-100 sm:text-3xl" style={{ animationDelay: "0.2s" }}>{L("Bersama Membentuk Generasi Cemerlang", "Together Shaping An Excellent Generation")}</p>
        </div>

        {/* Tier 1 — CEO */}
        <div className="pmm-pop mt-12 text-center" style={{ animationDelay: "0.2s" }} data-testid="org-ceo">
          <div className="mx-auto mb-4 flex w-fit items-center gap-1.5 rounded-full bg-gradient-to-r from-yellow-300 to-yellow-500 px-4 py-1 font-display text-[11px] font-extrabold uppercase tracking-widest text-navy-950 shadow-lg shadow-yellow-500/30">
            <Crown className="h-3.5 w-3.5" /> {L("Pengasas", "Founder")}
          </div>
          <ArchPortrait src={CEO.photo} alt={CEO.name} size="lg" onClick={() => setLightbox(CEO.poster)} testId="org-ceo-photo" />
          <div className="mt-5 font-serif-display text-3xl font-extrabold uppercase leading-none text-white sm:text-4xl">{CEO.name}</div>
          <span className="mt-2 inline-block rounded-md bg-gradient-to-r from-orange-700 to-orange-500 px-3 py-1 font-display text-sm font-extrabold uppercase tracking-wide text-white shadow-lg">{ms ? CEO.role.ms : CEO.role.en}</span>
          <div className="mt-1.5 text-xs font-semibold uppercase tracking-wide text-white/70">{CEO.org}</div>
        </div>

        {/* Penyambung CEO -> pengurusan */}
        <VLine className="mt-6 h-10" />
        <div className="relative mx-auto hidden h-[3px] w-2/3 rounded-full bg-gradient-to-r from-yellow-600 via-yellow-300 to-yellow-600 md:block" />

        {/* Tier 2 — Pengurusan */}
        <div className="grid gap-8 md:grid-cols-3 md:gap-6" data-testid="org-managers">
          {team.map((m, i) => {
            const contact = managers?.[m.key];
            return (
              <div key={m.key} className="relative text-center">
                <VLine className="hidden h-8 md:block" />
                {i > 0 && <VLine className="mb-4 h-8 md:hidden" />}
                <div className="pmm-pop group rounded-[1.75rem] border border-yellow-400/30 bg-white/[0.06] px-4 pb-5 pt-6 shadow-2xl backdrop-blur transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-yellow-400/70" style={{ animationDelay: `${0.3 + i * 0.1}s` }} data-testid={`org-manager-${m.key}`}>
                  <ArchPortrait src={m.photo} alt={m.name} onClick={() => setLightbox(m.poster)} testId={`org-manager-${m.key}-photo`} />
                  <div className="mt-4 font-display text-lg font-extrabold uppercase leading-tight text-white sm:text-xl">{m.name}</div>
                  <span className="mt-1.5 inline-block rounded-md bg-gradient-to-r from-orange-700 to-orange-500 px-2.5 py-0.5 font-display text-xs font-extrabold uppercase tracking-wide text-white">{ms ? m.role.ms : m.role.en}</span>
                  <div className="mt-2 flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wide text-yellow-300">
                    <m.icon className="h-4 w-4" /> {ms ? m.area.ms : m.area.en}
                  </div>
                  {contact?.wa && (
                    <a href={contact.wa} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-yellow-400/60 px-4 py-1.5 text-xs font-bold text-white transition-colors hover:bg-yellow-400 hover:text-navy-950" data-testid={`org-manager-${m.key}-wa`}>
                      <MessageCircle className="h-3.5 w-3.5" /> <span className="num">{contact.phoneDisplay}</span>
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Penyambung pengurusan -> tutor */}
        <VLine className="mt-8 h-10" />
        <div className="mx-auto flex w-fit items-center gap-2 rounded-full border-2 border-yellow-400 bg-gradient-to-r from-navy-800 to-navy-700 px-5 py-2 font-display text-sm font-extrabold uppercase tracking-widest text-white shadow-xl" data-testid="org-tutor-label">
          <Users className="h-4 w-4 text-yellow-300" /> Tutor PMM
        </div>
        <VLine className="h-6" />

        {/* Tier 3 — Tutor */}
        <div className="rounded-[2rem] border border-yellow-400/25 bg-white/[0.04] p-5 sm:p-7" data-testid="org-tutors">
          <div className="grid grid-cols-3 gap-x-3 gap-y-6 sm:gap-x-5 lg:grid-cols-6">
            {ONLINE_TUTORS.map((t, i) => (
              <button key={t.slug} type="button" onClick={() => setLightbox(`/landing/tutors/poster-${t.slug}.jpg`)} className="pmm-pop group text-center" style={{ animationDelay: `${0.05 * i}s` }} data-testid={`org-tutor-${t.slug}`}>
                <div className="relative mx-auto h-20 w-20 rounded-full bg-gradient-to-br from-yellow-200 via-yellow-500 to-yellow-700 p-[3px] shadow-lg transition-transform duration-300 group-hover:-translate-y-1 group-hover:scale-105 sm:h-28 sm:w-28">
                  <div className="h-full w-full overflow-hidden rounded-full bg-gradient-to-b from-white to-navy-200">
                    <img src={`/landing/tutors/${t.slug}.webp`} alt={t.name} loading="lazy" className="h-full w-full scale-[1.35] object-cover object-top pt-1" style={{ transformOrigin: "50% 12%" }} />
                  </div>
                </div>
                <div className="mt-2 font-display text-xs font-extrabold uppercase leading-tight text-white sm:text-sm">{t.name}</div>
                <div className="mx-auto mt-0.5 line-clamp-2 max-w-[9rem] text-[9px] font-semibold uppercase leading-tight text-yellow-200/80 sm:text-[10px]">{ms ? t.creds[0].ms : t.creds[0].en}</div>
              </button>
            ))}
          </div>
        </div>

        <p className="mt-8 text-center text-sm font-semibold text-white/70">
          {L("Tekan mana-mana gambar untuk lihat poster penuh", "Tap any photo to view the full poster")}
        </p>
      </div>
      <div className="h-1 w-full bg-gradient-to-r from-transparent via-yellow-400 to-transparent" />
    </section>
  );
}
